// Lifecycle ribbon — 11 segments rendered as a thin coloured strip.
// Generic primitive: the parent passes the current stage and the set of
// transition targets reachable from it; clicks fire the onSelect handler
// with the picked stage id (whether or not it's reachable). The ribbon
// itself never silently no-ops — the parent decides whether to commit,
// confirm, or surface an invalid-transition toast.
//
// Design tokens come from src/index.css; segment colours follow the DS
// (accent for current non-terminal, ok for terminal-good, danger for
// terminal-bad, text-5 for passed, white/[0.06] for upcoming).

import React from 'react';
import clsx from 'clsx';

export type RibbonTerminal = 'good' | 'bad';

export interface LifecycleRibbonStage {
  id: string;
  label: string;
  terminal?: RibbonTerminal;
}

export interface LifecycleRibbonProps {
  stages: readonly LifecycleRibbonStage[];
  currentStageId: string;
  /** Stage ids reachable in one transition from `currentStageId`. */
  reachableStageIds?: readonly string[];
  onSelect?: (stageId: string) => void;
  /** Tooltip for non-reachable segments. Defaults to "Not a valid transition from <currentLabel>". */
  notReachableHint?: (stageId: string, currentLabel: string) => string;
  className?: string;
}

const SEGMENT_HEIGHT_PX = 26;

export const LifecycleRibbon: React.FC<LifecycleRibbonProps> = ({
  stages,
  currentStageId,
  reachableStageIds,
  onSelect,
  notReachableHint,
  className,
}) => {
  const currentIdx = stages.findIndex((s) => s.id === currentStageId);
  const currentStage = stages[currentIdx];
  const reachable = new Set(reachableStageIds ?? []);

  return (
    <div className={className}>
      <div className="flex gap-1">
        {stages.map((stage, idx) => {
          const isCurrent = stage.id === currentStageId;
          const isPassed = idx < currentIdx;
          const isReachable = isCurrent || reachable.has(stage.id);
          const segClass = isCurrent
            ? stage.terminal === 'good'
              ? 'bg-ok'
              : stage.terminal === 'bad'
                ? 'bg-danger'
                : 'bg-accent'
            : isPassed
              ? 'bg-slate-600'
              : 'bg-white/[0.06]';
          const tooltip = isReachable
            ? stage.label
            : notReachableHint
              ? notReachableHint(stage.id, currentStage?.label ?? currentStageId)
              : `Not a valid transition from ${currentStage?.label ?? currentStageId}`;
          return (
            <button
              key={stage.id}
              type="button"
              onClick={() => onSelect?.(stage.id)}
              title={tooltip}
              aria-label={`${stage.label}${isCurrent ? ' (current)' : ''}`}
              aria-current={isCurrent ? 'step' : undefined}
              disabled={!onSelect}
              className={clsx(
                'flex-1 rounded-ds-sm border-0 transition-all duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
                segClass,
                isCurrent && 'ring-1 ring-accent ring-offset-2 ring-offset-surface-1',
                isReachable
                  ? 'cursor-pointer opacity-100 hover:-translate-y-0.5'
                  : 'cursor-not-allowed opacity-[0.35]',
              )}
              style={{ height: SEGMENT_HEIGHT_PX }}
            />
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between gap-1 font-mono text-[10px]">
        {stages.map((s) => (
          <span
            key={s.id}
            className={clsx(
              'flex-1 text-center',
              s.id === currentStageId ? 'text-slate-50' : 'text-slate-500',
            )}
          >
            {s.label.split(' ')[0]}
          </span>
        ))}
      </div>
    </div>
  );
};

export default LifecycleRibbon;
